/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

const getDxAPI = () => {
  const iframe = document.querySelectorAll("iframe")[0];
  if (iframe) {
    const { contentWindow } = iframe;
    if (contentWindow) {
      const { AdbeDx } = contentWindow;
      if (AdbeDx) {
        return { AdbeDx };
      }
    }
  }

  return {};
};

export const dxStreamStart = () =>
  new Promise((resolve, reject) => {
    const { AdbeDx } = getDxAPI();
    if (AdbeDx) {
      AdbeDx.sdkStreamStart();
      resolve();
      return;
    }
    reject();
  });

export const sendChunkToDxStream = chunk =>
  new Promise((resolve, reject) => {
    const { AdbeDx } = getDxAPI();
    if (AdbeDx) {
      AdbeDx.sdkSendChunk(chunk);
      resolve();
      return;
    }
    reject();
  });

export const dxStreamEnd = () =>
  new Promise((resolve, reject) => {
    const { AdbeDx } = getDxAPI();
    if (AdbeDx) {
      AdbeDx.sdkStreamEnd();
      resolve();
      return;
    }
    reject();
  });

/**
 * Continuously reads data from `dataSource` and streams it to DX, until `dataSource` has no more data.
 * @param {object} dataSource - source from which data is to be read.
 * @returns Promise - which resolves when all data from the `dataSource` has been read.
 */
export const streamDataToDx = dataSource =>
  new Promise((resolve, reject) => {
    dataSource
      .read()
      .then(({ hasData, data }) => {
        if (!hasData) {
          resolve(hasData);
          return;
        }

        sendChunkToDxStream(data);
        resolve(hasData);
      })
      .catch(e => reject(e));
  }).then(hasData => {
    if (hasData) {
      streamDataToDx(dataSource);
    }
  });

export const scrollIntoView = (elementId, offset) => {
  const { AdbeDx } = getDxAPI();
  if (AdbeDx) {
    AdbeDx.navSync.scrollIntoView(elementId, offset);
  }
};

export const getTopElementAndOffsetInViewport = () => {
  const { AdbeDx } = getDxAPI();
  if (AdbeDx) {
    return AdbeDx.navSync.getTopElementAndOffsetInViewport();
  }
};

export const setProfile = x => {
  const { AdbeDx } = getDxAPI();
  if (AdbeDx) {
    AdbeDx.personalization.setProfile(JSON.stringify(x));
  }
};

export const highlightFindResult = searchResult => {
  const { AdbeDx } = getDxAPI();
  if (AdbeDx && AdbeDx.find) {
    AdbeDx.find.highlightFindResult(searchResult);
  }
};

export const clearFind = () => {
  const { AdbeDx } = getDxAPI();
  if (AdbeDx && AdbeDx.find) {
    AdbeDx.find.clear();
  }
};
